import { createClient } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from './info';

// ⚠️ SAFETY NET: Detect IndexedDB issues and provide fallback
let isIndexedDBAvailable = true;

// Test IndexedDB availability
try {
  if (typeof window !== 'undefined' && 'indexedDB' in window) {
    const testDB = indexedDB.open('supabase-test-db', 1);
    testDB.onerror = () => {
      if (import.meta.env.DEV) {
        console.warn('⚠️ IndexedDB not available - falling back to localStorage');
      }
      isIndexedDBAvailable = false;
    };
  } else {
    isIndexedDBAvailable = false;
  }
} catch (e) {
  if (import.meta.env.DEV) {
    console.warn('⚠️ IndexedDB check failed - falling back to localStorage', e);
  }
  isIndexedDBAvailable = false;
}

// Android (browser + PWA): use localStorage so login persists and works after PWA update
function isAndroid(): boolean {
  if (typeof window === 'undefined') return false;
  return /android/i.test(navigator.userAgent);
}

const useLocalStorageForAuth = !isIndexedDBAvailable || isAndroid();

// Create Supabase client with storage fallback
export const supabase = createClient(
  `https://${projectId}.supabase.co`,
  publicAnonKey,
  {
    auth: {
      storage: useLocalStorageForAuth ? window.localStorage : undefined,
      storageKey: 'talentfunn-auth',
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: true,
    },
  }
);

// Export helper to check if we're in degraded mode
export const isDegradedMode = useLocalStorageForAuth;

if (useLocalStorageForAuth && typeof window !== 'undefined') {
  if (import.meta.env.DEV) {
    if (!isIndexedDBAvailable) {
      console.warn('⚠️ Talentfunn running in degraded mode (localStorage fallback)');
    } else if (isAndroid()) {
      console.warn('💡 Talentfunn: Using localStorage for auth on Android for reliable login');
    }
  }
}
